import { Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import CompactQuoteForm from "@/components/forms/CompactQuoteForm";
import { TERM_COMPARISON } from "@/data/siteContent";

const POINTS = [
  {
    title: "What Term Life Insurance Is",
    body: "Term life insurance provides coverage for a selected period — commonly 10, 15, 20, or 30 years. If you pass away during the term, your beneficiary receives the death benefit. Because it covers a defined window of time, term life usually provides more coverage per premium dollar than permanent insurance.",
  },
  {
    title: "Level-Term Coverage",
    body: "Most term policies sold today are level term: the death benefit and the premium are designed to stay the same for the entire term. When the term ends, coverage ends unless you renew (usually at a much higher premium) or use a conversion option to move to a permanent policy, if your contract includes one.",
  },
  {
    title: "Common Uses",
    body: "People often choose term life to replace income during working years, protect a mortgage, cover debts, or make sure children are provided for until they are grown. The right term length and benefit amount depend on what you are protecting and for how long.",
  },
  {
    title: "Term vs. Permanent Insurance",
    body: "Term covers a period of years and generally builds no cash value. Permanent insurance — like final expense whole life — is designed to last a lifetime and may build modest cash value. Many families use each for a different job. Michael can help you compare what fits.",
  },
  {
    title: "What Affects the Cost",
    body: "Age, health, tobacco use, coverage amount, and term length all affect pricing. Some term policies require a medical exam; others use simplified underwriting with health questions. Options vary by carrier, which is one reason comparing companies matters.",
  },
];

export default function TermLife() {
  return (
    <>
      <SEO
        title="Term Life Insurance Explained | 1907 Insurance Group"
        description="Understand term life insurance — term lengths, level coverage, conversion options, and how it compares to final expense whole life. Plain-language guidance from Michael Janas."
      />
      <PageHero
        eyebrow="Term Life Insurance"
        title="Big Protection for the Years That Matter Most"
        intro="Term life insurance can provide a larger benefit for a defined period — often at an initially lower premium — to protect income, a mortgage, or a growing family."
      />

      <section className="py-16 md:py-24" data-testid="term-education">
        <div className="container-site max-w-4xl space-y-14">
          {POINTS.map((s, i) => (
            <Reveal key={s.title}>
              <h2 className="text-2xl sm:text-3xl tracking-tight" data-testid={`term-section-${i}`}>{s.title}</h2>
              <p className="mt-4 text-lg text-mutedink leading-relaxed">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-mist py-16 md:py-24" data-testid="term-comparison">
        <div className="container-site">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl tracking-tight">Term Life vs. Final Expense Whole Life</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-x-auto rounded-xl border border-navy-100 bg-white shadow-sm">
              <table className="w-full min-w-[640px] text-left" data-testid="comparison-table">
                <thead>
                  <tr className="bg-navy text-white">
                    <th scope="col" className="p-4 sm:p-5 font-sans text-base font-semibold">Feature</th>
                    <th scope="col" className="p-4 sm:p-5 font-sans text-base font-semibold">Term Life</th>
                    <th scope="col" className="p-4 sm:p-5 font-sans text-base font-semibold">Final Expense Whole Life</th>
                  </tr>
                </thead>
                <tbody>
                  {TERM_COMPARISON.map((r, i) => (
                    <tr key={r.feature} className={i % 2 ? "bg-navy-50/50" : "bg-white"}>
                      <th scope="row" className="p-4 sm:p-5 font-sans text-base font-semibold text-navy">{r.feature}</th>
                      <td className="p-4 sm:p-5 text-mutedink">{r.term}</td>
                      <td className="p-4 sm:p-5 text-mutedink">{r.fe}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-5 text-sm text-mutedink">Actual features vary by policy and insurer. This table is a general comparison only.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="term-tips">
        <div className="container-site grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl tracking-tight">Choosing the Right Term and Amount</h2>
            <ul className="mt-6 space-y-4">
              {[
                "Match the term length to the obligation — for example, the years left on a mortgage or until children are independent",
                "Choose a benefit amount that reflects income, debts, and future needs — not a guess",
                "Ask about conversion options before you buy, in case your needs change",
                "Review renewal pricing — renewing after the term is usually far more expensive than expected",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-lg text-mutedink leading-relaxed">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <CompactQuoteForm title="Request a Term Life Quote" />
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
